'use client'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Meter } from '@/components/ui/meter'
import { motion } from 'framer-motion'
import confetti from 'canvas-confetti'
import { StarsBackground } from '@/components/ui/stars-background'
import { Progress } from '@/components/ui/progress'
import { Particles } from './particles'
import { useTheme } from '@/context/ThemeContext'

const funSteps = [
  "Checking if your deck has enough rocket ship emojis 🚀",
  "Counting how many times you used 'disruptive' and 'innovative'",
  "Making sure your TAM is at least a trillion dollars",
  "Verifying your hockey stick growth projections 📈",
  "Ensuring your valuation is Web3-AI-Metaverse compatible",
  "Calculating how many times you mentioned 'platform'",
  "Checking if you have the mandatory 'Why Now?' slide",
  "Measuring the boldness of your vision statement",
  "Validating your 'conservative' market share estimates",
  "Confirming your exit strategy includes unicorn status 🦄",
]

interface AnalysisProgressProps {
  startImmediately?: boolean;
  progress?: number;
  analysisId?: number;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({ startImmediately = false, analysisId }) => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const [isAnalyzing, setIsAnalyzing] = useState(startImmediately)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [color, setColor] = useState(theme === 'dark' ? '#ffffff' : '#000000')

  useEffect(() => {
    if (startImmediately) {
      startAnalysis()
    }
    return () => {
      console.log('AnalysisProgress unmounting')
    }
  }, [startImmediately])

  useEffect(() => {
    if (progress >= 100) {
      triggerConfetti()
    }
  }, [progress])

  useEffect(() => {
  }, [currentStep])

  useEffect(() => {
    setColor(theme === 'dark' ? '#ffffff' : '#000000')
  }, [theme])

  const triggerConfetti = () => {
    const duration = 3 * 1000
    const end = Date.now() + duration

    const colors = ['#4F46E5', '#818CF8', '#C7D2FE', '#34D399', '#F472B6']

    ;(function frame() {
      confetti({
        particleCount: 7,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      })
      confetti({
        particleCount: 7,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }())

    setTimeout(() => {
      if (analysisId) {
        navigate(`/analysis/${analysisId}`)
      } else {
        console.error('Analysis ID is undefined, cannot navigate')
      }
    }, duration + 500)
  }

  const startAnalysis = () => {
    setIsAnalyzing(true)

    const intervalId = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 0.5
        if (newProgress >= 100) {
          clearInterval(intervalId)
          return 100
        }
        return newProgress
      })
      
      if (progress % 30 === 0) {
        setCurrentStep(prev => (prev + 1) % funSteps.length)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }

  return (
    <div className="fixed inset-0 bg-white dark:bg-zinc-950 z-50 flex items-center justify-center overflow-hidden">
      <Particles
        className="absolute inset-0"
        quantity={300}
        ease={80}
        color={color}
        staticity={50}
        size={0.3}
      />
      <div className="relative z-10 max-w-xl w-full text-center px-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          Analyzing Your Pitch Deck
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 transition-opacity duration-1000">
          {funSteps[currentStep]}
        </p>
        <div className="w-80 mx-auto">
          <Progress value={progress} className="h-2" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {progress.toFixed(0)}% Complete
          </p>
        </div>
      </div>
    </div>
  );
};
